import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Home, Building2, Warehouse, ShoppingBag, Factory, Hotel, GraduationCap, Heart, CheckCircle, ShoppingCart, Camera, Shield, Wifi, HardDrive, Star, ChevronRight } from 'lucide-react';

type PropertyType = 'house' | 'office' | 'apartment' | 'hotel' | 'restaurant' | 'factory' | 'warehouse' | 'school' | 'hospital';

interface ProductItem {
  category: string;
  model: string;
  brand: string;
  qty: number;
  price: number;
  note: string;
  priority: 'essential' | 'recommended' | 'optional';
}

interface Recommendation {
  total: number;
  items: ProductItem[];
  summary: string;
  features: string[];
}

const propertyTypes = [
  { id: 'house', label: 'House', icon: Home, color: 'from-blue-400 to-blue-600' },
  { id: 'office', label: 'Office', icon: Building2, color: 'from-violet-400 to-violet-600' },
  { id: 'apartment', label: 'Apartment', icon: Home, color: 'from-cyan-400 to-cyan-600' },
  { id: 'hotel', label: 'Hotel', icon: Hotel, color: 'from-pink-400 to-rose-600' },
  { id: 'restaurant', label: 'Restaurant', icon: ShoppingBag, color: 'from-[#7FA86A] to-[#658C53]' },
  { id: 'factory', label: 'Factory', icon: Factory, color: 'from-red-400 to-red-600' },
  { id: 'warehouse', label: 'Warehouse', icon: Warehouse, color: 'from-amber-400 to-amber-600' },
  { id: 'school', label: 'School', icon: GraduationCap, color: 'from-green-400 to-green-600' },
  { id: 'hospital', label: 'Hospital', icon: Heart, color: 'from-rose-400 to-red-600' },
];

const recommendations: Record<PropertyType, Recommendation> = {
  house: {
    total: 285000,
    summary: 'Complete smart home security package with CCTV, alarm, and smart devices.',
    features: ['Mobile remote access', '24/7 motion alerts', 'Smart door lock', 'Color night vision'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2547G2-LS 4MP ColorVu', brand: 'Hikvision', qty: 4, price: 18500, note: 'Entrance + Perimeter', priority: 'essential' },
      { category: 'NVR', model: 'Hikvision DS-7608NXI-K1/8P 8CH', brand: 'Hikvision', qty: 1, price: 45000, note: '8-channel PoE NVR', priority: 'essential' },
      { category: 'Hard Drive', model: 'WD Purple 2TB Surveillance HDD', brand: 'WD', qty: 1, price: 18000, note: '30-day storage', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox SP65 Control Panel', brand: 'Paradox', qty: 1, price: 35000, note: '8-zone alarm system', priority: 'recommended' },
      { category: 'Smart Home', model: 'Ezviz Smart Door Lock DL01S', brand: 'Ezviz', qty: 1, price: 28500, note: 'Fingerprint + app', priority: 'optional' },
      { category: 'Networking', model: 'TP-Link Deco XE75 WiFi 6E', brand: 'TP-Link', qty: 1, price: 42000, note: 'Mesh WiFi whole home', priority: 'recommended' },
    ],
  },
  office: {
    total: 640000,
    summary: 'Enterprise security system with AI analytics, access control, and structured networking.',
    features: ['AI face detection', 'Access control', 'Centralized monitoring', 'Network management'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2T47G2-L 4MP', brand: 'Hikvision', qty: 8, price: 22000, note: 'Indoor + Outdoor', priority: 'essential' },
      { category: 'NVR', model: 'Hikvision DS-7616NXI-K2/16P 16CH', brand: 'Hikvision', qty: 1, price: 95000, note: '4K AI NVR', priority: 'essential' },
      { category: 'Hard Drive', model: 'WD Purple 4TB × 2 (RAID)', brand: 'WD', qty: 2, price: 28000, note: '60-day RAID-1', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox EVO192 Enterprise', brand: 'Paradox', qty: 1, price: 65000, note: 'Multi-zone enterprise', priority: 'essential' },
      { category: 'Networking', model: 'MikroTik CCR2004 Router', brand: 'MikroTik', qty: 1, price: 48000, note: 'Enterprise routing', priority: 'essential' },
      { category: 'WiFi', model: 'UniFi U6 Pro Access Point', brand: 'Ubiquiti', qty: 4, price: 38500, note: 'WiFi 6 enterprise AP', priority: 'recommended' },
    ],
  },
  apartment: {
    total: 145000,
    summary: 'Compact security solution for apartments with smart cameras and app control.',
    features: ['Compact design', 'Cloud storage', 'App monitoring', 'Two-way audio'],
    items: [
      { category: 'Camera', model: 'Ezviz C6N 2K Indoor WiFi', brand: 'Ezviz', qty: 2, price: 12500, note: 'Indoor smart cameras', priority: 'essential' },
      { category: 'Camera', model: 'Ezviz H3C 3K Outdoor', brand: 'Ezviz', qty: 1, price: 18500, note: 'Entrance/Balcony', priority: 'essential' },
      { category: 'DVR', model: 'Hikvision DS-7104HGHI-K1 4CH', brand: 'Hikvision', qty: 1, price: 22000, note: 'Compact 4-channel', priority: 'essential' },
      { category: 'Smart Home', model: 'Ezviz Video Doorbell DB10', brand: 'Ezviz', qty: 1, price: 22500, note: 'Smart doorbell + chime', priority: 'recommended' },
      { category: 'Networking', model: 'TP-Link Archer AX73 WiFi 6', brand: 'TP-Link', qty: 1, price: 28000, note: 'High-speed router', priority: 'optional' },
    ],
  },
  hotel: {
    total: 1850000,
    summary: 'Complete hotel security ecosystem with PTZ cameras, networking, and guest WiFi.',
    features: ['PTZ overview cameras', 'Guest WiFi management', 'LPR parking', 'Central monitoring room'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2T87G2-L 8MP', brand: 'Hikvision', qty: 24, price: 32000, note: 'Corridors + Perimeter', priority: 'essential' },
      { category: 'PTZ Camera', model: 'Dahua SD49425XB-HNR 4MP PTZ', brand: 'Dahua', qty: 4, price: 85000, note: 'Lobby + Parking overview', priority: 'essential' },
      { category: 'NVR', model: 'Dahua NVR608-64-XI 64CH', brand: 'Dahua', qty: 1, price: 185000, note: 'AI enterprise NVR', priority: 'essential' },
      { category: 'Networking', model: 'MikroTik CRS326 Layer-3 Switch', brand: 'MikroTik', qty: 2, price: 78000, note: 'Core switching', priority: 'essential' },
      { category: 'WiFi', model: 'Ubiquiti UniFi U6 Lite', brand: 'Ubiquiti', qty: 16, price: 28000, note: 'Per floor coverage', priority: 'essential' },
      { category: 'Starlink', model: 'Starlink Business (Hardware)', brand: 'Starlink', qty: 1, price: 185000, note: 'Backup connectivity', priority: 'recommended' },
    ],
  },
  restaurant: {
    total: 320000,
    summary: 'Restaurant security with kitchen monitoring, POS area, and customer counting.',
    features: ['Customer counting', 'Kitchen safety', 'POS protection', 'After-hours alerts'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2547G2-LS ColorVu', brand: 'Hikvision', qty: 6, price: 18500, note: 'Indoor color 24/7', priority: 'essential' },
      { category: 'NVR', model: 'Hikvision DS-7608NXI-K1/8P', brand: 'Hikvision', qty: 1, price: 45000, note: '8CH PoE NVR', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox MG5050 Wireless', brand: 'Paradox', qty: 1, price: 42000, note: 'After-hours protection', priority: 'essential' },
      { category: 'Smart Home', model: 'Ezviz DB10 Video Doorbell', brand: 'Ezviz', qty: 2, price: 22500, note: 'Front & back door', priority: 'recommended' },
      { category: 'Networking', model: 'TP-Link EAP610 WiFi 6 AP', brand: 'TP-Link', qty: 2, price: 32000, note: 'Guest WiFi', priority: 'recommended' },
    ],
  },
  factory: {
    total: 2800000,
    summary: 'Industrial-grade security with explosion-proof cameras, perimeter protection, and AI analytics.',
    features: ['PPE detection AI', 'Perimeter intrusion', 'Fire anomaly detection', 'Access control integration'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2T87G2-L 8MP', brand: 'Hikvision', qty: 16, price: 32000, note: 'Production & perimeter', priority: 'essential' },
      { category: 'Camera (Hazardous)', model: 'Dahua IPC-HFW5449T Explosion-Proof', brand: 'Dahua', qty: 8, price: 125000, note: 'Hazardous zones', priority: 'essential' },
      { category: 'NVR', model: 'Hikvision DS-96128NI-I24 128CH', brand: 'Hikvision', qty: 1, price: 485000, note: 'Enterprise 128CH', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox EVO192 + Fire Panel', brand: 'Paradox', qty: 1, price: 125000, note: 'Integrated alarm+fire', priority: 'essential' },
      { category: 'Networking', model: 'MikroTik CCR2116 + Fiber', brand: 'MikroTik', qty: 1, price: 185000, note: 'Industrial backbone', priority: 'essential' },
    ],
  },
  warehouse: {
    total: 1200000,
    summary: 'High-bay warehouse cameras with 360° PTZ, perimeter fence detection, and theft prevention.',
    features: ['High-bay coverage', 'Forklift detection', 'After-hours patrol', 'Loading dock LPR'],
    items: [
      { category: 'Camera', model: 'Dahua IPC-HFW5849T-ASE-LED Ultra', brand: 'Dahua', qty: 12, price: 45000, note: 'High-bay ultra-wide', priority: 'essential' },
      { category: 'PTZ Camera', model: 'Dahua SD49425XB-HNR PTZ', brand: 'Dahua', qty: 2, price: 85000, note: 'Overview PTZ patrol', priority: 'essential' },
      { category: 'NVR', model: 'Dahua NVR608-32-XI AI NVR', brand: 'Dahua', qty: 1, price: 185000, note: '32CH AI NVR', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox EVO192 Perimeter', brand: 'Paradox', qty: 1, price: 85000, note: 'Fence + perimeter', priority: 'essential' },
      { category: 'Networking', model: 'MikroTik CRS328 + Fiber', brand: 'MikroTik', qty: 1, price: 125000, note: 'Industrial network', priority: 'essential' },
    ],
  },
  school: {
    total: 485000,
    summary: 'Campus security with student safety features, crowd monitoring, and entry control.',
    features: ['Student entry tracking', 'Crowd analytics', 'Emergency response', 'Visitor management'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2T47G2-L 4MP', brand: 'Hikvision', qty: 12, price: 22000, note: 'Campus coverage', priority: 'essential' },
      { category: 'NVR', model: 'Hikvision DS-7616NXI-K2/16P', brand: 'Hikvision', qty: 1, price: 95000, note: '16CH AI NVR', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox SP65 + Panic Buttons', brand: 'Paradox', qty: 1, price: 55000, note: 'Emergency response', priority: 'essential' },
      { category: 'Networking', model: 'UniFi Campus WiFi Package', brand: 'Ubiquiti', qty: 1, price: 185000, note: 'Full campus WiFi 6', priority: 'recommended' },
    ],
  },
  hospital: {
    total: 1850000,
    summary: 'Healthcare-grade security with HIPAA considerations, ICU monitoring, and infant protection.',
    features: ['Patient safety monitoring', 'Staff tracking', 'Pharmacy protection', 'Emergency response'],
    items: [
      { category: 'Camera', model: 'Hikvision DS-2CD2T87G2-L 8MP', brand: 'Hikvision', qty: 20, price: 32000, note: 'Corridors + Reception', priority: 'essential' },
      { category: 'Camera (Dome)', model: 'Dahua IPC-HDW3849H Panoramic', brand: 'Dahua', qty: 8, price: 45000, note: 'Waiting areas', priority: 'essential' },
      { category: 'NVR', model: 'Dahua NVR608-64-XI 64CH AI', brand: 'Dahua', qty: 1, price: 185000, note: '64-channel enterprise', priority: 'essential' },
      { category: 'Alarm', model: 'Paradox EVO192 + Medical Alert', brand: 'Paradox', qty: 1, price: 125000, note: 'Code blue integration', priority: 'essential' },
      { category: 'Networking', model: 'Cisco Catalyst Layer-3 Switch', brand: 'Cisco', qty: 2, price: 185000, note: 'Medical-grade network', priority: 'essential' },
    ],
  },
};

const priorityBadge: Record<string, string> = {
  essential: 'bg-red-100 text-red-700',
  recommended: 'bg-blue-100 text-blue-700',
  optional: 'bg-gray-100 text-gray-600',
};

export default function AIRecommendPage() {
  const [selected, setSelected] = useState<PropertyType | null>(null);
  const [result, setResult] = useState<Recommendation | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [cart, setCart] = useState<string[]>([]);

  const handleSelect = async (type: PropertyType) => {
    setSelected(type);
    setIsAnalyzing(true);
    await new Promise(r => setTimeout(r, 1500));
    setResult(recommendations[type]);
    setIsAnalyzing(false);
  };

  const toggleCart = (model: string) => {
    setCart(prev => prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model]);
  };

  const selectedTotal = result?.items
    .filter(item => cart.includes(item.model))
    .reduce((sum, item) => sum + item.price * item.qty, 0) || 0;

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-200">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">AI Product Recommendation</h1>
          <p className="text-xs text-gray-500">Select property type · Get tailored product bundle</p>
        </div>
      </div>

      {/* Property Type Grid */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <p className="text-sm font-medium text-gray-700 mb-4">What type of property do you need to secure?</p>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-3">
          {propertyTypes.map(pt => {
            const Icon = pt.icon;
            return (
              <button
                key={pt.id}
                onClick={() => handleSelect(pt.id as PropertyType)}
                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${
                  selected === pt.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${pt.color} flex items-center justify-center shadow-sm`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[11px] font-medium text-gray-700 text-center">{pt.label}</span>
                {selected === pt.id && <CheckCircle className="w-3 h-3 text-green-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading */}
      {isAnalyzing && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>
            <Sparkles className="w-8 h-8 text-green-500" />
          </motion.div>
          <p className="text-sm text-gray-600 mt-3 font-medium">Generating AI recommendations…</p>
          <p className="text-xs text-gray-400 mt-1">Analyzing product compatibility, pricing, and project requirements</p>
        </motion.div>
      )}

      {/* Results */}
      <AnimatePresence>
        {result && !isAnalyzing && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Summary Banner */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-semibold">AI Recommendation Ready</span>
                  </div>
                  <p className="text-sm opacity-90">{result.summary}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs opacity-80">Est. Budget</p>
                  <p className="text-xl font-bold">LKR {result.total.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {result.features.map((f, i) => (
                  <span key={i} className="text-[11px] bg-white/20 px-2.5 py-1 rounded-full">{f}</span>
                ))}
              </div>
            </div>

            {/* Product List */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Recommended Products</h3>
                <span className="text-xs text-gray-500">{result.items.length} items</span>
              </div>
              <div className="divide-y divide-gray-100">
                {result.items.map((item, i) => (
                  <div key={i} className={`flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors ${cart.includes(item.model) ? 'bg-green-50' : ''}`}>
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      {item.category.toLowerCase().includes('camera') || item.category.toLowerCase().includes('ptز') ? <Camera className="w-5 h-5 text-gray-600" /> :
                       item.category.toLowerCase().includes('alarm') ? <Shield className="w-5 h-5 text-gray-600" /> :
                       item.category.toLowerCase().includes('network') || item.category.toLowerCase().includes('wifi') ? <Wifi className="w-5 h-5 text-gray-600" /> :
                       item.category.toLowerCase().includes('hard') ? <HardDrive className="w-5 h-5 text-gray-600" /> :
                       item.category.toLowerCase().includes('starlink') ? <Star className="w-5 h-5 text-gray-600" /> :
                       <Sparkles className="w-5 h-5 text-gray-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase">{item.category}</span>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${priorityBadge[item.priority]}`}>
                          {item.priority}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">{item.model}</p>
                      <p className="text-xs text-gray-500">{item.brand} · {item.note}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">LKR {(item.price).toLocaleString()}</p>
                      <p className="text-xs text-gray-400">× {item.qty} = LKR {(item.price * item.qty).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => toggleCart(item.model)}
                      className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                        cart.includes(item.model)
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      {cart.includes(item.model) ? <CheckCircle className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Summary */}
            {cart.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-green-200 p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{cart.length} products selected</p>
                  <p className="text-xs text-gray-500">Total: LKR {selectedTotal.toLocaleString()}</p>
                </div>
                <div className="flex gap-3">
                  <button className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
                    Save List
                  </button>
                  <button className="flex items-center gap-2 text-sm text-white bg-gradient-to-r from-[#7FA86A] to-[#96BE82] px-5 py-2 rounded-xl hover:shadow-[rgba(127,168,106,0.2)] hover:shadow-lg transition-all">
                    Generate Quotation <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
